import { useState, useEffect, useCallback } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import {
  Loader2, Play, Pause, SkipForward, RotateCcw, AlertOctagon, CheckCircle,
  Clock, Activity, Shield, XCircle, Zap, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

export default function RollingPipelineStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [confirmInput, setConfirmInput] = useState("");
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [forceAdvanceCity, setForceAdvanceCity] = useState("");
  const [resetCheckpointsCity, setResetCheckpointsCity] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pipeline/dashboard`);
      setData(res.data);
      setLastRefreshedAt(Date.now());
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const doAction = async (url, body, label) => {
    setActionLoading(label);
    try {
      const res = await axios.post(`${API}${url}`, body);
      toast.success(`${label}: ${JSON.stringify(res.data)}`);
      fetchDashboard();
    } catch (err) {
      toast.error(`${label} failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading("");
    }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Unable to load pipeline dashboard</p>;

  const paused = data.pipeline_paused;
  const emergency = data.emergency_stopped;
  const tokens = data.job_tokens || {};
  const schedule = data.schedule || {};
  const cities = data.active_cities || [];
  const sourceHealth = data.source_health || [];
  const dlq = data.dead_letter_queue || {};
  const lastSweep = data.last_sweep_summary;
  const recentSteps = data.recent_nightly_steps || [];

  return (
    <div className="space-y-5" data-testid="rolling-pipeline-status-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetchDashboard} lastRefreshedAt={lastRefreshedAt} testId="rolling-pipeline-refresh" /></div>
      {/* Status Banner */}
      <div className={`rounded-xl border-2 p-4 flex items-center justify-between ${
        emergency ? "bg-red-50 border-red-400" : paused ? "bg-amber-50 border-amber-400" : "bg-emerald-50 border-emerald-400"
      }`} data-testid="pipeline-status-banner">
        <div className="flex items-center gap-3">
          {emergency ? <AlertOctagon className="w-6 h-6 text-red-600" /> :
           paused ? <Pause className="w-6 h-6 text-amber-600" /> :
           <Activity className="w-6 h-6 text-emerald-600" />}
          <div>
            <p className="font-bold text-sm" data-testid="pipeline-status-label">
              {emergency ? "EMERGENCY STOPPED" : paused ? "PAUSED" : "RUNNING"}
            </p>
            <p className="text-xs text-slate-500">
              {data.last_sweep_time ? `Last sweep: ${new Date(data.last_sweep_time).toLocaleString()}` : "No sweep data yet"}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!emergency && (
            <button
              onClick={() => doAction("/admin/pipeline/pause", { paused: !paused }, paused ? "Resume" : "Pause")}
              disabled={!!actionLoading}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                paused ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-amber-500 text-white hover:bg-amber-600"
              }`}
              data-testid="pipeline-pause-btn"
            >
              {paused ? <><Play className="w-3 h-3 inline mr-1" />Resume</> : <><Pause className="w-3 h-3 inline mr-1" />Pause</>}
            </button>
          )}
          {emergency && (
            <button
              onClick={() => doAction("/admin/pipeline/lift-emergency", {}, "Lift Emergency")}
              disabled={!!actionLoading}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="pipeline-lift-emergency-btn"
            >
              <Play className="w-3 h-3 inline mr-1" />Lift Emergency
            </button>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Active Cities" value={cities.length} icon={<Activity className="w-4 h-4" />} color="sky" testId="stat-active-cities" />
        <StatCard label="Job Tokens" value={tokens.total || 0} sub={`${tokens.completed || 0} done, ${tokens.failed || 0} failed`} icon={<Shield className="w-4 h-4" />} color="violet" testId="stat-job-tokens" />
        <StatCard label="DLQ Items" value={dlq.pending || 0} sub={`${dlq.retrying || 0} retrying`} icon={<XCircle className="w-4 h-4" />} color={dlq.pending > 0 ? "red" : "emerald"} testId="stat-dlq" />
        <StatCard label="Source Health" value={sourceHealth.filter(s => (s.consecutive_zero_nights || 0) >= 2).length} sub="sources flagged" icon={<Zap className="w-4 h-4" />} color={sourceHealth.filter(s => (s.consecutive_zero_nights || 0) >= 2).length > 0 ? "amber" : "emerald"} testId="stat-source-health" />
      </div>

      {/* Schedule */}
      <div className="rounded-xl border border-slate-200 p-4">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Nightly Schedule (Pacific Time)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(schedule.nightly || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
              <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="font-mono font-semibold text-slate-700">{s.time}</span>
              <span className="text-slate-500 truncate">{s.job}</span>
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-4 text-xs text-slate-400">
          <span>Weekly: {schedule.weekly || "—"}</span>
          <span>Monthly: {schedule.monthly || "—"}</span>
        </div>
        <div className="mt-2 flex gap-4 text-xs text-slate-400">
          <span>Last weekly: {data.last_weekly_run ? new Date(data.last_weekly_run).toLocaleDateString() : "Never"}</span>
          <span>Last monthly: {data.last_monthly_run ? new Date(data.last_monthly_run).toLocaleDateString() : "Never"}</span>
        </div>
      </div>

      {/* Last Sweep Summary */}
      {lastSweep && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="last-sweep-summary">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Last Sweep Summary</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
            {[
              ["P1 Cities", lastSweep.phase1_cities],
              ["P2 Cities", lastSweep.phase2_cities],
              ["P3 Cities", lastSweep.phase3_cities],
              ["P4 Cities", lastSweep.phase4_cities],
              ["Discovered", lastSweep.entities_discovered],
              ["Enriched", lastSweep.entities_enriched],
              ["P6 Cities", lastSweep.phase6_cities],
              ["P7 Cities", lastSweep.phase7_cities],
              ["Birthday Offers", lastSweep.birthday_offers_found],
              ["Errors", lastSweep.errors?.length || 0],
            ].map(([label, val]) => (
              <div key={label} className="bg-slate-50 rounded-lg p-2">
                <p className="text-lg font-bold text-slate-800">{val ?? 0}</p>
                <p className="text-[10px] text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          {lastSweep.errors?.length > 0 && (
            <div className="mt-2 p-2 bg-red-50 rounded-lg">
              <p className="text-[10px] font-bold text-red-600 mb-1">Errors:</p>
              {lastSweep.errors.slice(0, 5).map((e, i) => (
                <p key={i} className="text-[10px] text-red-500 truncate">{e}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent Step Logs */}
      {recentSteps.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Recent Nightly Steps</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {recentSteps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] bg-slate-50 rounded px-2 py-1">
                <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                <span className="font-mono text-slate-600">{step.event_type?.replace("nightly_step_", "")}</span>
                <span className="text-slate-400 ml-auto">{step.timestamp ? new Date(step.timestamp).toLocaleTimeString() : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Cities Table */}
      {cities.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="active-cities-table">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Active Cities ({cities.length})</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="text-left py-1.5 px-2">City</th>
                  <th className="text-center py-1.5 px-2">Phase</th>
                  <th className="text-center py-1.5 px-2">Night</th>
                  <th className="text-center py-1.5 px-2">Completion</th>
                  <th className="text-center py-1.5 px-2">Slot</th>
                </tr>
              </thead>
              <tbody>
                {cities.slice(0, 15).map((c) => (
                  <tr key={c.city_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-1.5 px-2 font-medium text-slate-700">{c.city_name}, {c.state_code}</td>
                    <td className="py-1.5 px-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        c.current_phase <= 3 ? "bg-sky-100 text-sky-700" :
                        c.current_phase <= 5 ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>P{c.current_phase}</span>
                    </td>
                    <td className="py-1.5 px-2 text-center text-slate-600">{c.sweep_night}</td>
                    <td className="py-1.5 px-2 text-center">
                      <div className="w-full bg-slate-200 rounded-full h-1.5 inline-block max-w-[60px]">
                        <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${c.completion_pct}%` }} />
                      </div>
                      <span className="ml-1 text-[10px] text-slate-500">{c.completion_pct}%</span>
                    </td>
                    <td className="py-1.5 px-2 text-center">{c.rolling_pipeline_slot || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Source Health */}
      {sourceHealth.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-4" data-testid="source-health-table">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">Source Health Monitor</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {sourceHealth.map((s) => {
              const flagged = (s.consecutive_zero_nights || 0) >= 2;
              return (
                <div key={s.source_name} className={`rounded-lg border p-2 text-center ${flagged ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                  <p className="text-[10px] font-bold uppercase text-slate-500">{s.source_name}</p>
                  <p className={`text-sm font-bold ${flagged ? "text-red-600" : "text-emerald-600"}`}>
                    {s.consecutive_zero_nights || 0} zeros
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Manual Controls */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="pipeline-controls">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Manual Controls</p>

        {/* Force Advance */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={forceAdvanceCity}
            onChange={(e) => setForceAdvanceCity(e.target.value)}
            placeholder="City ID to force advance"
            className="flex-1 text-xs border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            data-testid="force-advance-input"
          />
          <button
            onClick={() => { if (forceAdvanceCity) doAction("/admin/pipeline/force-advance", { city_id: forceAdvanceCity }, "Force Advance"); }}
            disabled={!forceAdvanceCity || !!actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
            data-testid="force-advance-btn"
          >
            <SkipForward className="w-3 h-3 inline mr-1" />Advance
          </button>
        </div>

        {/* Reset Checkpoints */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={resetCheckpointsCity}
            onChange={(e) => setResetCheckpointsCity(e.target.value)}
            placeholder="City ID to reset checkpoints"
            className="flex-1 text-xs border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            data-testid="reset-checkpoints-input"
          />
          <button
            onClick={() => { if (resetCheckpointsCity) doAction("/admin/pipeline/reset-city-checkpoints", { city_id: resetCheckpointsCity }, "Reset Checkpoints"); }}
            disabled={!resetCheckpointsCity || !!actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-600 text-white hover:bg-slate-700 disabled:opacity-50"
            data-testid="reset-checkpoints-btn"
          >
            <RotateCcw className="w-3 h-3 inline mr-1" />Reset
          </button>
        </div>

        {/* Manual Triggers */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => doAction("/admin/pipeline/trigger-nightly", {}, "Trigger Nightly")}
            disabled={!!actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
            data-testid="trigger-nightly-btn"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" />Run Nightly
          </button>
          <button
            onClick={() => doAction("/admin/pipeline/trigger-weekly", {}, "Trigger Weekly")}
            disabled={!!actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50"
            data-testid="trigger-weekly-btn"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" />Run Weekly
          </button>
          <button
            onClick={() => doAction("/admin/pipeline/trigger-monthly", {}, "Trigger Monthly")}
            disabled={!!actionLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50"
            data-testid="trigger-monthly-btn"
          >
            <RefreshCw className="w-3 h-3 inline mr-1" />Run Monthly
          </button>
        </div>

        {/* Emergency Stop */}
        <div className="pt-2 border-t border-slate-200">
          {!showEmergencyModal ? (
            <button
              onClick={() => setShowEmergencyModal(true)}
              className="px-4 py-2 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 transition-colors"
              data-testid="emergency-stop-open-btn"
            >
              <AlertOctagon className="w-4 h-4 inline mr-1" />Emergency Stop
            </button>
          ) : (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-4 space-y-3" data-testid="emergency-stop-modal">
              <p className="text-sm font-bold text-red-700">Are you sure? This will halt ALL sweep operations immediately.</p>
              <p className="text-xs text-red-600">Type <span className="font-mono font-bold">CONFIRM</span> to proceed:</p>
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder="Type CONFIRM"
                className="w-full text-sm border-2 border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 font-mono"
                data-testid="emergency-stop-confirm-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (confirmInput === "CONFIRM") {
                      doAction("/admin/pipeline/emergency-stop", { confirm_text: "CONFIRM" }, "Emergency Stop");
                      setShowEmergencyModal(false);
                      setConfirmInput("");
                    } else {
                      toast.error("You must type CONFIRM exactly");
                    }
                  }}
                  disabled={confirmInput !== "CONFIRM" || !!actionLoading}
                  className="px-4 py-2 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
                  data-testid="emergency-stop-confirm-btn"
                >
                  <AlertOctagon className="w-3 h-3 inline mr-1" />STOP ALL SWEEPS
                </button>
                <button
                  onClick={() => { setShowEmergencyModal(false); setConfirmInput(""); }}
                  className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-200 text-slate-700 hover:bg-slate-300"
                  data-testid="emergency-stop-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon, color, testId }) {
  const colors = {
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    red: "bg-red-50 border-red-200 text-red-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${colors[color] || colors.sky}`} data-testid={testId}>
      <div className="flex items-center justify-center gap-1 mb-1">{icon}</div>
      <p className="text-2xl font-black">{value}</p>
      <p className="text-[10px] font-semibold uppercase tracking-wider">{label}</p>
      {sub && <p className="text-[9px] opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}
